import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { queryCache, CACHE_TTL } from '../lib/cache';
import { Competition } from '../types';
import { handleError } from '../lib/errorHandler';
import { hasValidWcEnrollmentEntry, isWcKickoffPassed } from '../utils/wcEntryAccess';

export interface EntryStatus {
  entryId: string;
  entryNickname: string;
  entryNumber: number;
  isKnockedOut: boolean;
  needsPick: boolean;
  currentGameweekSurvived: number;
}

export interface EnrolledCompetition extends Competition {
  hasAccess: boolean;
  hasPicks: boolean;
  hasPredictions: boolean;
  pickCount: number;
  predictionCount: number;
  participantCount: number;
  remainingParticipantCount?: number; // participants not knocked out (for LMS)
  currentRank?: number;
  isKnockedOut?: boolean;
  currentGameweekSurvived?: number;
  needsAction: boolean;
  nextDeadline?: Date;
  // Multi-entry support
  entryCount?: number;
  activeEntryCount?: number; // entries not knocked out
  entriesNeedingPicks?: number; // entries that need picks for next gameweek
  entries?: EntryStatus[]; // per-entry status details
  // World Cup entry breakdown (only populated for worldcup competitions)
  wcSubmittedCount?: number;
  wcInProgressCount?: number;
}

export const useUserCompetitionEnrollment = () => {
  const [enrolledCompetitions, setEnrolledCompetitions] = useState<EnrolledCompetition[]>([]);
  const [availableCompetitions, setAvailableCompetitions] = useState<Competition[]>([]);
  const [firstGameweekDeadlines, setFirstGameweekDeadlines] = useState<Record<string, Date>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, currentOrganization } = useAuth();
  const isMountedRef = useRef(true);

  const fetchEnrollments = useCallback(async (options?: { force?: boolean }) => {
    if (!user?.id || !currentOrganization?.id) {
      setEnrolledCompetitions([]);
      setAvailableCompetitions([]);
      setFirstGameweekDeadlines({});
      setLoading(false);
      return;
    }

    const cacheKey = queryCache.generateKey('user_competition_enrollment_v2', {
      userId: user.id,
      orgId: currentOrganization.id
    });

    if (options?.force) {
      queryCache.invalidateByParams('user_competition_enrollment_v2', {
        userId: user.id,
        orgId: currentOrganization.id,
      });
    }

    try {
      setError(null);

      // Stale-while-revalidate: surface cached data immediately (even if expired)
      // so the dashboard renders without waiting on the 14-query batch.
      // Skip when force-refreshing so pull-to-refresh / post-deactivate always hits the network.
      const stale = options?.force
        ? null
        : queryCache.getStale<{
            enrolled: EnrolledCompetition[];
            available: Competition[];
            firstGameweekDeadlines?: Record<string, string>;
          }>(cacheKey);
      if (stale && isMountedRef.current) {
        setEnrolledCompetitions(stale.data.enrolled);
        setAvailableCompetitions(stale.data.available);
        const deadlineMap: Record<string, Date> = {};
        Object.entries(stale.data.firstGameweekDeadlines || {}).forEach(([id, iso]) => {
          deadlineMap[id] = new Date(iso);
        });
        setFirstGameweekDeadlines(deadlineMap);
        setLoading(false);
        if (!stale.isStale) {
          // Fresh enough — skip the network round trip entirely.
          return;
        }
      } else {
        setLoading(true);
      }

      const result = await queryCache.deduplicate(
        cacheKey,
        async () => {
          // Single RPC replaces 14 round-trips. Returns one JSON payload with
          // raw data; aggregation/business logic stays in JS below for safety.
          const { data: rpcData, error: rpcError } = await supabase.rpc(
            'get_user_competition_enrollment_data',
            { _user_id: user.id, _organization_id: currentOrganization.id }
          );

          if (rpcError) {
            throw rpcError;
          }

          const payload = (rpcData || {}) as any;
          const allCompetitions: any[] = payload.competitions || [];

          if (allCompetitions.length === 0) {
            return { enrolled: [], available: [] };
          }

          const competitionIds = allCompetitions.map((c: any) => c.id);

          // Entry-based totals (matches public cards). Access rows miss free/pay-on-submit joins.
          const { data: entryStatsData } = await supabase.rpc('get_public_competition_stats', {
            _competition_ids: competitionIds,
          });
          const entryStatsMap = new Map<string, { total: number; remaining: number | null }>();
          ((entryStatsData as any[]) || []).forEach((row) => {
            entryStatsMap.set(row.competition_id, {
              total: row.total_entries ?? 0,
              remaining: row.remaining_entries,
            });
          });

          // Reshape RPC payload into the {data,...}/{count,...} envelopes
          // the original code expects, so the rest of the function is unchanged.
          const accessResult = { data: payload.access || [], error: null };
          const picksResult = { data: payload.picks || [], error: null };
          const predictionsResult = { data: payload.predictions || [], error: null };
          const statusResult = { data: payload.status || [], error: null };
          const participantResult = { data: payload.participants || [], error: null };
          const gamesResult = { data: payload.games || [], error: null };
          const entriesResult = { data: payload.entries || [], error: null };
          const allStatusResult = { data: payload.all_status || [], error: null };
          const wcPredictionsResult = { data: payload.wc_predictions || [], error: null };
          const wcEarliestGameResult = {
            data: payload.wc_earliest_game ? [payload.wc_earliest_game] : [],
            error: null
          };
          const wcTotalGamesResult = {
            data: null,
            error: null,
            count: payload.wc_total_games || 0
          };
          const wcAllEntriesResult = { data: payload.wc_all_entries || [], error: null };
          const wcSubmittedMetaResult = { data: payload.wc_submitted_meta || [], error: null };
          const wcFirstGroupKickoff = payload.wc_first_group_kickoff as string | null | undefined;

          const accessMap = new Map(
            (accessResult.data || []).map(a => [a.competition_id, true])
          );

          const picksMap = new Map<string, any[]>();
          (picksResult.data || []).forEach(pick => {
            const existing = picksMap.get(pick.competition_id) || [];
            picksMap.set(pick.competition_id, [...existing, pick]);
          });

          const predictionsMap = new Map<string, any[]>();
          (predictionsResult.data || []).forEach(pred => {
            const existing = predictionsMap.get(pred.competition_id) || [];
            predictionsMap.set(pred.competition_id, [...existing, pred]);
          });

          // World Cup predictions map
          const wcPredictionsMap = new Map<string, boolean>();
          (wcPredictionsResult.data || []).forEach((pred: any) => {
            wcPredictionsMap.set(pred.competition_id, true);
          });

          // Group status by competition and entry
          const statusMap = new Map<string, any[]>();
          (statusResult.data || []).forEach(s => {
            const existing = statusMap.get(s.competition_id) || [];
            statusMap.set(s.competition_id, [...existing, s]);
          });

          const participantMap = new Map<string, number>();
          (participantResult.data || []).forEach(access => {
            const count = participantMap.get(access.competition_id) || 0;
            participantMap.set(access.competition_id, count + 1);
          });

          // Group entries by competition
          const entriesMap = new Map<string, any[]>();
          (entriesResult.data || []).forEach(entry => {
            const existing = entriesMap.get(entry.competition_id) || [];
            entriesMap.set(entry.competition_id, [...existing, entry]);
          });

          // Count remaining participants (those NOT knocked out) per competition.
          // Prefer entry totals from get_public_competition_stats when available.
          const remainingParticipantMap = new Map<string, number>();
          const allStatuses = allStatusResult.data || [];
          
          // Group all statuses by competition
          const allStatusByComp = new Map<string, any[]>();
          allStatuses.forEach((s: any) => {
            const existing = allStatusByComp.get(s.competition_id) || [];
            allStatusByComp.set(s.competition_id, [...existing, s]);
          });
          
          // For each competition, count unique entries/users that are not knocked out
          competitionIds.forEach((compId: string) => {
            const entryStats = entryStatsMap.get(compId);
            const totalParticipants = entryStats?.total ?? participantMap.get(compId) ?? 0;

            if (entryStats && entryStats.remaining !== null && entryStats.remaining !== undefined) {
              remainingParticipantMap.set(compId, entryStats.remaining);
              return;
            }

            const compStatuses = allStatusByComp.get(compId) || [];
            
            if (compStatuses.length === 0) {
              // No knockout status records yet - all participants are remaining
              remainingParticipantMap.set(compId, totalParticipants);
            } else {
              // Entries without a status row are still in; subtract knocked-out only.
              const knockedOutEntries = new Set(
                compStatuses.filter((s: any) => s.is_knocked_out).map((s: any) => s.entry_id || s.user_id)
              );
              remainingParticipantMap.set(compId, Math.max(0, totalParticipants - knockedOutEntries.size));
            }
          });

          // For predictor competitions, find the next gameweek's first kickoff
          // For LMS competitions, find the next individual game
          const nextDeadlineMap = new Map<string, Date>();
          const nextGameweekMap = new Map<string, number>();
          const nextGameIdMap = new Map<string, string>();
          const firstGameweekDeadlineMap = new Map<string, Date>();
          // Track the current gameweek that's in progress (some games started, some still upcoming)
          const currentInProgressGameweekMap = new Map<string, number>();
          const now = new Date();

          // Group games by competition and gameweek
          const competitionGames = new Map<string, any[]>();
          (gamesResult.data || []).forEach((compGame: any) => {
            if (!competitionGames.has(compGame.competition_id)) {
              competitionGames.set(compGame.competition_id, []);
            }
            if (compGame.games) {
              competitionGames.get(compGame.competition_id)!.push(compGame.games);
            }
          });

          // Calculate deadline for each competition
          allCompetitions.forEach((comp: any) => {
            // For World Cup competitions, use earliest upcoming wc_games match
            if (comp.competition_type === 'worldcup') {
              const wcFirstGame = wcEarliestGameResult.data?.[0];
              if (wcFirstGame && new Date(wcFirstGame.match_date) > now) {
                nextDeadlineMap.set(comp.id, new Date(wcFirstGame.match_date));
              }
              return;
            }

            const games = competitionGames.get(comp.id) || [];
            const compStartDate = new Date(comp.start_date);
            const compEndDate = comp.end_date ? new Date(comp.end_date) : null;

            // Filter games within competition date range
            const gamesInRange = games.filter((g: any) => {
              const matchDate = new Date(g.match_date);
              return matchDate >= compStartDate && (!compEndDate || matchDate <= compEndDate);
            });

            // Both predictor and LMS: find the next COMPLETE gameweek (where no games have started)
            // Group games by gameweek
            const gamesByWeek = new Map<number, any[]>();
            gamesInRange.forEach((g: any) => {
              if (g.gameweek) {
                if (!gamesByWeek.has(g.gameweek)) {
                  gamesByWeek.set(g.gameweek, []);
                }
                gamesByWeek.get(g.gameweek)!.push(g);
              }
            });

            // Find the first gameweek where ALL games are still upcoming
            const sortedWeeks = Array.from(gamesByWeek.keys()).sort((a, b) => a - b);

            // Store the first gameweek's deadline for join eligibility check
            if (sortedWeeks.length > 0) {
              const firstWeek = sortedWeeks[0];
              const firstWeekGames = gamesByWeek.get(firstWeek)!;
              const firstGame = firstWeekGames.reduce((earliest: any, g: any) =>
                !earliest || new Date(g.match_date) < new Date(earliest.match_date) ? g : earliest
              , null);
              if (firstGame) {
                firstGameweekDeadlineMap.set(comp.id, new Date(firstGame.match_date));
              }
            }

            for (const week of sortedWeeks) {
              const weekGames = gamesByWeek.get(week)!;
              const allUpcoming = weekGames.every((g: any) => g.status === 'upcoming');
              const someUpcoming = weekGames.some((g: any) => g.status === 'upcoming');
              const someStartedOrFinished = weekGames.some((g: any) => g.status !== 'upcoming');
              const firstGame = weekGames.reduce((earliest: any, g: any) =>
                !earliest || new Date(g.match_date) < new Date(earliest.match_date) ? g : earliest
              , null);

              // Track current in-progress gameweek (mix of started/finished and upcoming)
              if (someUpcoming && someStartedOrFinished && comp.competition_type === 'lms') {
                currentInProgressGameweekMap.set(comp.id, week);
              }

              // If all games are upcoming and the first game is in the future
              if (allUpcoming && firstGame && new Date(firstGame.match_date) > now) {
                nextDeadlineMap.set(comp.id, new Date(firstGame.match_date));
                nextGameweekMap.set(comp.id, week);
                if (comp.competition_type === 'lms') {
                  nextGameIdMap.set(comp.id, firstGame.id);
                }
                break;
              }
            }
          });

          const enrolled: EnrolledCompetition[] = [];
          const available: Competition[] = [];

          allCompetitions.forEach((comp: any) => {
            // Defense in depth: RPC already filters is_active=true, but skip inactive
            // so stale payloads or future RPC changes cannot leak into My Competitions.
            if (comp.is_active === false) {
              return;
            }

            const hasAccess = accessMap.has(comp.id);
            const picks = picksMap.get(comp.id) || [];
            const predictions = predictionsMap.get(comp.id) || [];
            const rawStatus = statusMap.get(comp.id);
            const userEntries = entriesMap.get(comp.id) || [];

            const hasPicks = picks.length > 0;
            const hasPredictions = predictions.length > 0;
            const hasWcPredictions = wcPredictionsMap.has(comp.id);
            // Having a competition_entries row (e.g. auto-created free WC entry)
            // counts as enrollment so the comp surfaces under "My Competitions".
            const hasEntries = userEntries.length > 0;

            const wcKickoffPassed =
              comp.competition_type === 'worldcup' &&
              isWcKickoffPassed(comp, wcFirstGroupKickoff);
            const wcEntryFeeEnabled = comp.entry_fee_enabled === true;

            let isEnrolled =
              hasAccess || hasPicks || hasPredictions || hasWcPredictions || hasEntries;

            // Paid World Cup after kickoff: only users with a paid/free entry count
            // as enrolled. Pending-only users must not appear in My Competitions.
            if (wcKickoffPassed && wcEntryFeeEnabled) {
              isEnrolled = hasValidWcEnrollmentEntry(userEntries, {
                entryFeeEnabled: true,
              });
            }

            // Only use status data if user is actually enrolled
            // This prevents showing stale/orphaned status for competitions they haven't entered
            const status = isEnrolled ? rawStatus : undefined;

            const baseCompetition: Competition = {
              id: comp.id,
              organizationId: comp.organization_id,
              name: comp.name,
              competitionType: comp.competition_type,
              startDate: new Date(comp.start_date),
              endDate: comp.end_date ? new Date(comp.end_date) : null,
              status: comp.status,
              isActive: comp.is_active !== false,
              createdAt: new Date(comp.created_at),
              updatedAt: new Date(comp.updated_at),
              skippedGameweeks: comp.skipped_gameweeks || [],
              endGameScenario: comp.end_game_scenario || 'last_man_standing',
              entryFeeEnabled: comp.entry_fee_enabled || false,
              entryFeeAmount: comp.entry_fee_amount || 0,
              entryFeeCurrency: comp.entry_fee_currency || 'gbp',
              prizeDistributionType: comp.prize_distribution_type,
              topXPlaces: comp.top_x_places,
              monthlyPrizesEnabled: comp.monthly_prizes_enabled,
              monthlyMinFixtures: comp.monthly_min_fixtures,
              // Prize pot fields
              calculatedPrizePot: comp.calculated_prize_pot || 0,
              manualPrizePotOverride: comp.manual_prize_pot_override,
              initialPrizePot: comp.initial_prize_pot || 0,
              prizePotCurrency: comp.prize_pot_currency || comp.entry_fee_currency || 'gbp',
              // Revenue sharing fields
              organizationRevenuePercentage: comp.organization_revenue_percentage,
              prizePotPercentage: comp.prize_pot_percentage
            };

            if (isEnrolled) {
              const nextDeadline = nextDeadlineMap.get(comp.id);
              const nextGameweek = nextGameweekMap.get(comp.id);
              const nextGameId = nextGameIdMap.get(comp.id);
              const statuses = status || [];
              const entries = entriesMap.get(comp.id) || [];
              
              // For multi-entry LMS: calculate per-entry status
              const entryCount = entries.length || 1; // At least 1 if they have picks
              
              // Calculate how many entries are NOT knocked out
              const activeEntryCount = comp.competition_type === 'lms' && entries.length > 0
                ? entries.filter((entry: any) => {
                    const entryStatus = statuses.find((s: any) => s.entry_id === entry.id);
                    return !entryStatus?.is_knocked_out;
                  }).length
                : (statuses.some((s: any) => s.is_knocked_out) ? 0 : 1);
              
              // User is fully knocked out only if ALL entries are knocked out
              const isKnockedOut = comp.competition_type === 'lms' && entries.length > 0
                ? entries.every((entry: any) => {
                    const entryStatus = statuses.find((s: any) => s.entry_id === entry.id);
                    return entryStatus?.is_knocked_out === true;
                  })
                : statuses.length > 0 && statuses.every((s: any) => s.is_knocked_out);
              
              // Get max gameweek survived across all entries
              const currentGameweekSurvived = statuses.length > 0
                ? Math.max(...statuses.map((s: any) => s.current_gameweek_survived || 0))
                : 0;

              // Determine if action is needed
              let needsAction = false;
              let entriesNeedingPicks = 0;
              
              if (comp.competition_type === 'worldcup' && nextDeadline) {
                // For World Cup: action needed if user hasn't predicted all games
                const totalWcGames = wcTotalGamesResult.count || 0;
                const userWcPredictions = (wcPredictionsResult.data || []).filter(
                  (p: any) => p.competition_id === comp.id
                ).length;
                needsAction = userWcPredictions < totalWcGames;
              } else if (!isKnockedOut && nextDeadline && nextGameweek) {
                if (comp.competition_type === 'predictor') {
                  // Check if user has predictions for all games in the next gameweek
                  const gameweekGames = competitionGames.get(comp.id)?.filter(
                    (g: any) => g.gameweek === nextGameweek && g.status === 'upcoming'
                  ) || [];
                  const gameweekGameIds = new Set(gameweekGames.map((g: any) => g.id));
                  const predictedGameIds = new Set(
                    predictions
                      .filter((p: any) => p.competition_id === comp.id)
                      .map((p: any) => p.game_id)
                  );
                  needsAction = gameweekGames.length > 0 &&
                    !gameweekGames.every((g: any) => predictedGameIds.has(g.id));
                } else {
                  // For LMS: check if user has made picks for ALL active entries
                  // First check if there's a current in-progress gameweek
                  const currentInProgressGW = currentInProgressGameweekMap.get(comp.id);
                  
                  // If there's a current gameweek in progress, check if user has picks for it
                  // If they do, no action needed until that gameweek completes
                  if (currentInProgressGW) {
                    if (entries.length > 0) {
                      // Multi-entry: check each active entry for the current in-progress gameweek
                      entries.forEach((entry: any) => {
                        const entryStatus = statuses.find((s: any) => s.entry_id === entry.id);
                        const entryIsKnockedOut = entryStatus?.is_knocked_out === true;
                        
                        if (!entryIsKnockedOut) {
                          const hasPickForCurrentGW = picks.some((p: any) =>
                            p.competition_id === comp.id && 
                            p.gameweek === currentInProgressGW &&
                            p.entry_id === entry.id
                          );
                          if (!hasPickForCurrentGW) {
                            entriesNeedingPicks++;
                          }
                        }
                      });
                      needsAction = entriesNeedingPicks > 0;
                    } else {
                      // Single entry (legacy): check if any pick exists for this gameweek
                      const hasPick = picks.some((p: any) =>
                        p.competition_id === comp.id && p.gameweek === currentInProgressGW
                      );
                      needsAction = !hasPick;
                      entriesNeedingPicks = hasPick ? 0 : 1;
                    }
                  } else {
                    // No in-progress gameweek, check for next gameweek
                    // Check if there are any games in gameweeks before the next gameweek
                    // that are not yet completed (either upcoming or in_progress)
                    const hasPreviousGameweekInProgress = (competitionGames.get(comp.id) || []).some(
                      (g: any) => g.gameweek < nextGameweek && g.status !== 'finished'
                    );

                    if (!hasPreviousGameweekInProgress) {
                      if (entries.length > 0) {
                        // Multi-entry: check each active entry
                        entries.forEach((entry: any) => {
                          const entryStatus = statuses.find((s: any) => s.entry_id === entry.id);
                          const entryIsKnockedOut = entryStatus?.is_knocked_out === true;
                          
                          if (!entryIsKnockedOut) {
                            const hasPickForEntry = picks.some((p: any) =>
                              p.competition_id === comp.id && 
                              p.gameweek === nextGameweek &&
                              p.entry_id === entry.id
                            );
                            if (!hasPickForEntry) {
                              entriesNeedingPicks++;
                            }
                          }
                        });
                        needsAction = entriesNeedingPicks > 0;
                      } else {
                        // Single entry (legacy): check if any pick exists for this gameweek
                        const hasPick = picks.some((p: any) =>
                          p.competition_id === comp.id && p.gameweek === nextGameweek
                        );
                        needsAction = !hasPick;
                        entriesNeedingPicks = hasPick ? 0 : 1;
                      }
                    }
                  }
                }
              }

              // Build per-entry status array for LMS competitions
              const currentInProgressGW = currentInProgressGameweekMap.get(comp.id);
              const relevantGameweek = currentInProgressGW || nextGameweek;
              
              const entryStatuses: EntryStatus[] = (comp.competition_type === 'lms' || comp.competition_type === 'worldcup') && entries.length > 0
                ? entries.map((entry: any) => {
                    if (comp.competition_type === 'worldcup') {
                      // For World Cup: check how many predictions this entry has
                      const entryPredictionCount = (wcPredictionsResult.data || []).filter(
                        (p: any) => p.competition_id === comp.id && p.entry_id === entry.id
                      ).length;
                      const totalWcGames = wcTotalGamesResult.count || 0;
                      return {
                        entryId: entry.id,
                        entryNickname: entry.entry_nickname,
                        entryNumber: entry.entry_number,
                        isKnockedOut: false,
                        needsPick: entryPredictionCount < totalWcGames,
                        currentGameweekSurvived: 0
                      };
                    }
                    // LMS logic
                    const entryStatus = statuses.find((s: any) => s.entry_id === entry.id);
                    const entryIsKnockedOut = entryStatus?.is_knocked_out === true;
                    const hasPickForEntry = relevantGameweek ? picks.some((p: any) =>
                      p.competition_id === comp.id && 
                      p.gameweek === relevantGameweek &&
                      p.entry_id === entry.id
                    ) : true;
                    
                    return {
                      entryId: entry.id,
                      entryNickname: entry.entry_nickname,
                      entryNumber: entry.entry_number,
                      isKnockedOut: entryIsKnockedOut,
                      needsPick: !entryIsKnockedOut && !hasPickForEntry,
                      currentGameweekSurvived: entryStatus?.current_gameweek_survived || 0
                    };
                  }).sort((a, b) => a.entryNumber - b.entryNumber)
                : [];

              // For World Cup: count all entries to stay consistent with the leaderboard.
              // (Previously this filtered to "completed" entries after the deadline, which
              // caused the card count to drift from the leaderboard count.)
              let wcParticipantCount: number | undefined;
              let wcSubmittedCount: number | undefined;
              let wcInProgressCount: number | undefined;
              if (comp.competition_type === 'worldcup') {
                const allWcEntries = (wcAllEntriesResult.data || []).filter(
                  (e: any) => e.competition_id === comp.id
                );
                wcParticipantCount = allWcEntries.length;

                // Submitted entries: distinct entry_ids from wc_meta_predictions where submitted=true
                const submittedEntryIds = new Set(
                  (wcSubmittedMetaResult.data || [])
                    .filter((m: any) => m.competition_id === comp.id)
                    .map((m: any) => m.entry_id)
                );
                wcSubmittedCount = allWcEntries.filter((e: any) => submittedEntryIds.has(e.id)).length;
                wcInProgressCount = Math.max(0, wcParticipantCount - wcSubmittedCount);
              }

              enrolled.push({
                ...baseCompetition,
                hasAccess,
                hasPicks,
                hasPredictions,
                pickCount: picks.length,
                predictionCount: predictions.length,
                participantCount: wcParticipantCount ?? (entryStatsMap.get(comp.id)?.total ?? participantMap.get(comp.id) ?? 0),
                remainingParticipantCount: remainingParticipantMap.get(comp.id),
                wcSubmittedCount,
                wcInProgressCount,
                isKnockedOut,
                currentGameweekSurvived,
                needsAction,
                nextDeadline,
                entryCount,
                activeEntryCount,
                entriesNeedingPicks,
                entries: entryStatuses
              });
            } else {
              if (comp.competition_type === 'worldcup') {
                // World Cup competitions use nextDeadlineMap (populated from wc_games)
                const wcDeadline = nextDeadlineMap.get(comp.id);
                const canJoin = comp.status === 'upcoming' ||
                                (comp.status === 'active' && wcDeadline && wcDeadline > now);
                const isViewableOnly = comp.status === 'active' && (!wcDeadline || wcDeadline <= now);
                if (canJoin || isViewableOnly) {
                  available.push(baseCompetition);
                }
              } else {
                // LMS/Predictor: check firstGameweekDeadlineMap
                const firstGameweekDeadline = firstGameweekDeadlineMap.get(comp.id);
                const canJoin = comp.status === 'upcoming' ||
                                (comp.status === 'active' && firstGameweekDeadline && firstGameweekDeadline > now);
                const isViewableOnly = comp.status === 'active' && firstGameweekDeadline && firstGameweekDeadline <= now;
                if (canJoin || isViewableOnly) {
                  available.push(baseCompetition);
                }
              }
            }
          });

          enrolled.sort((a, b) => {
            if (a.needsAction && !b.needsAction) return -1;
            if (!a.needsAction && b.needsAction) return 1;
            const aTime = a.updatedAt instanceof Date ? a.updatedAt.getTime() : 0;
            const bTime = b.updatedAt instanceof Date ? b.updatedAt.getTime() : 0;
            return bTime - aTime;
          });

          const firstGameweekDeadlines: Record<string, string> = {};
          firstGameweekDeadlineMap.forEach((date, id) => {
            firstGameweekDeadlines[id] = date.toISOString();
          });

          return { enrolled, available, firstGameweekDeadlines };
        },
        CACHE_TTL.SHORT
      );

      if (isMountedRef.current) {
        setEnrolledCompetitions(result.enrolled);
        setAvailableCompetitions(result.available);
        const deadlineMap: Record<string, Date> = {};
        Object.entries(result.firstGameweekDeadlines || {}).forEach(([id, iso]) => {
          deadlineMap[id] = new Date(iso);
        });
        setFirstGameweekDeadlines(deadlineMap);
      }
    } catch (err) {
      console.error('Error fetching user competition enrollments:', err);
      handleError(err, {
        hook: 'useUserCompetitionEnrollment',
        action: 'fetchEnrollments',
        metadata: {
          userId: user?.id,
          organizationId: currentOrganization?.id
        }
      });
      if (isMountedRef.current) {
        setError('Failed to load your competitions');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [user?.id, currentOrganization?.id]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Track previous identity so we only reset state when user/org actually changes,
  // not on every mount (which kills back-button navigation performance).
  const prevIdentityRef = useRef<string | null>(null);

  useEffect(() => {
    const identity = `${user?.id || 'anon'}:${currentOrganization?.id || 'none'}`;
    const identityChanged = prevIdentityRef.current !== null && prevIdentityRef.current !== identity;

    if (identityChanged) {
      // Only clear state when switching users or organizations.
      setEnrolledCompetitions([]);
      setAvailableCompetitions([]);
      setFirstGameweekDeadlines({});
      setError(null);
    }

    prevIdentityRef.current = identity;
    fetchEnrollments();
  }, [fetchEnrollments, user?.id, currentOrganization?.id]);

  return {
    enrolledCompetitions,
    availableCompetitions,
    firstGameweekDeadlines,
    loading,
    error,
    refreshEnrollments: fetchEnrollments
  };
};
